if (!customElements.get('product-info')) {
  // Metafield values may be a bare Form ID or a pasted Globo shortcode — extract the ID either way.
  const extractQuoteFormId = (value) => {
    if (!value) return '';

    const trimmed = value.trim();
    const shortcodeMatch = trimmed.match(/data-id=["']([^"']+)["']/i);

    return shortcodeMatch ? shortcodeMatch[1].trim() : trimmed;
  };

  // Accept numeric IDs or base64-style IDs as returned by Powerful Form Builder shortcodes.
  const isValidQuoteFormId = (formId) => {
    if (!formId) return false;

    if (/^\d{1,10}$/.test(formId)) return true;

    return (
      /^[A-Za-z0-9+/]+={0,2}$/.test(formId) &&
      formId.length >= 4 &&
      formId.length <= 24
    );
  };

  // Globo rewrites the container's data-id from the base64 form ID to the decoded numeric ID when it
  // initializes, so IDs must be compared in numeric form.
  const normalizeQuoteFormId = (formId) => {
    if (!formId) return '';
    if (/^\d+$/.test(formId)) return formId;

    try {
      const decoded = atob(formId);
      return /^\d+$/.test(decoded) ? decoded : formId;
    } catch (error) {
      return formId;
    }
  };

  // Globo renders into the container on page load; an empty container means the ID has no matching form.
  const quoteFormHasRenderedContent = (formElement) => Boolean(formElement?.querySelector('form, .globo-form, .g-container, .globo-form-app'));

  // Resolve which popup form to show based on product.metafields.custom.quote_form_id.
  // Always falls back to the default form so buyers are never blocked from submitting.
  const getActiveQuoteForm = (productInfo) => {
    // The default form container is rendered by snippets/quote-form-popup.liquid, which resolves the
    // store-specific default form ID, so the container's data-id is the single source of truth here.
    const defaultForm = document.querySelector('.request-a-quote-form--default');
    const defaultFormId = defaultForm?.dataset?.id || '';
    const rawFormId = productInfo?.dataset?.quoteFormId || '';
    const formId = extractQuoteFormId(rawFormId);

    if (!isValidQuoteFormId(formId) || normalizeQuoteFormId(formId) === normalizeQuoteFormId(defaultFormId)) {
      return defaultForm;
    }

    const alternateForm = document.querySelector('.request-a-quote-form--alternate');

    // Only use the mapped form when Globo successfully rendered it at page load.
    if (
      alternateForm &&
      normalizeQuoteFormId(alternateForm.dataset.id) === normalizeQuoteFormId(formId) &&
      quoteFormHasRenderedContent(alternateForm)
    ) {
      return alternateForm;
    }

    return defaultForm;
  };

  // Only one quote form should be visible in the popup at a time.
  const hideAllQuoteForms = () => {
    document.querySelectorAll('.request-a-quote-form').forEach(form => form.classList.remove('show'));
  };

  customElements.define(
    'product-info',
    class ProductInfo extends HTMLElement {
      quantityInput = undefined;
      quantityForm = undefined;
      onVariantChangeUnsubscriber = undefined;
      cartUpdateUnsubscriber = undefined;
      abortController = undefined;
      pendingRequestUrl = null;
      preProcessHtmlCallbacks = [];
      postProcessHtmlCallbacks = [];

      constructor() {
        super();

        this.quantityInput = this.querySelector('.quantity__input');
      }

      connectedCallback() {
        this.initializeProductSwapUtility();

        this.initializeRequestAQuote();

        this.onVariantChangeUnsubscriber = subscribe(
          PUB_SUB_EVENTS.optionValueSelectionChange,
          this.handleOptionValueChange.bind(this),
        );

        this.initQuantityHandlers();
        this.dispatchEvent(
          new CustomEvent('product-info:loaded', { bubbles: true }),
        );
      }

      addPreProcessCallback(callback) {
        this.preProcessHtmlCallbacks.push(callback);
      }

      initializeRequestAQuote() {
        // Bind once per product-info instance; delegation covers buttons added after variant changes.
        if (this.dataset.requestQuoteBound === 'true') return;
        this.dataset.requestQuoteBound = 'true';

        this.addEventListener('click', (event) => {
          const requestAQuoteBtn = event.target.closest('.request-a-quote');
          if (!requestAQuoteBtn || !this.contains(requestAQuoteBtn)) return;

          const requestForm = getActiveQuoteForm(this);
          // Nothing to show if Globo has not rendered the form (embed slow, blocked, or form missing).
          if (!requestForm || !quoteFormHasRenderedContent(requestForm)) return;

          const rqOverlay = document.getElementById('rq-overlay');
          if (!rqOverlay) return;

          // Pre-fill fields when present; mapped forms may use a different field layout.
          const skuValue =
            document.getElementById('product-sku')?.innerHTML ?? '';
          const skuInput = requestForm.querySelector('.product-sku input');
          const titleValue =
            document.getElementById('product-title')?.innerText ?? '';
          const titleInput = requestForm.querySelector('.product-title input');

          if (skuInput) skuInput.value = `${(window.algoliaStrings && window.algoliaStrings.sku) || 'SKU:'}${skuValue}`;
          if (titleInput) titleInput.value = titleValue;

          hideAllQuoteForms();
          requestForm.classList.add('show');
          rqOverlay.classList.add('show');

          const pageHeight = Math.max(
            document.body.scrollHeight,
            document.documentElement.scrollHeight,
            document.body.offsetHeight,
            document.documentElement.offsetHeight,
            document.body.clientHeight,
            document.documentElement.clientHeight,
          );
          rqOverlay.style.height = `${pageHeight}px`;

          let closeButton = requestForm.querySelector('.rq-close-button');
          if (!closeButton) {
            closeButton = document.createElement('button');
            closeButton.classList.add('rq-close-button');
            closeButton.setAttribute(
              'aria-label',
              (window.variantStrings && window.variantStrings.closeRequestQuote) || 'Close request a quote popup',
            );
            requestForm.prepend(closeButton);
          }

          const closePopup = () => {
            requestForm.classList.remove('show');
            rqOverlay.classList.remove('show');
            if (skuInput) skuInput.value = '';
            if (titleInput) titleInput.value = '';
            rqOverlay.removeEventListener('click', handleOverlayClick);
            closeButton.removeEventListener('click', closePopup);
          };

          const handleOverlayClick = (overlayEvent) => {
            if (overlayEvent.target === rqOverlay) {
              closePopup();
            }
          };

          closeButton.addEventListener('click', closePopup);
          rqOverlay.addEventListener('click', handleOverlayClick);
        });
      }

      initQuantityHandlers() {
        if (!this.quantityInput) return;

        this.quantityForm = this.querySelector('.product-form__quantity');
        if (!this.quantityForm) return;

        this.setQuantityBoundries();
        if (!this.dataset.originalSection) {
          this.cartUpdateUnsubscriber = subscribe(
            PUB_SUB_EVENTS.cartUpdate,
            this.fetchQuantityRules.bind(this),
          );
        }
      }

      disconnectedCallback() {
        this.onVariantChangeUnsubscriber();
        this.cartUpdateUnsubscriber?.();
      }

      initializeProductSwapUtility() {
        this.preProcessHtmlCallbacks.push((html) =>
          html
            .querySelectorAll('.scroll-trigger')
            .forEach((element) =>
              element.classList.add('scroll-trigger--cancel'),
            ),
        );
        this.postProcessHtmlCallbacks.push((newNode) => {
          window?.Shopify?.PaymentButton?.init();
          window?.ProductModel?.loadShopifyXR();
        });
      }

      handleOptionValueChange({
        data: { event, target, selectedOptionValues },
      }) {
        if (!this.contains(event.target)) return;

        this.resetProductFormState();

        const productUrl =
          target.dataset.productUrl ||
          this.pendingRequestUrl ||
          this.dataset.url;
        this.pendingRequestUrl = productUrl;
        const shouldSwapProduct = this.dataset.url !== productUrl;
        const shouldFetchFullPage =
          this.dataset.updateUrl === 'true' && shouldSwapProduct;

        this.renderProductInfo({
          requestUrl: this.buildRequestUrlWithParams(
            productUrl,
            selectedOptionValues,
            shouldFetchFullPage,
          ),
          targetId: target.id,
          callback: shouldSwapProduct
            ? this.handleSwapProduct(productUrl, shouldFetchFullPage)
            : this.handleUpdateProductInfo(productUrl),
        });
      }

      resetProductFormState() {
        const productForm = this.productForm;
        productForm?.toggleSubmitButton(true);
        productForm?.handleErrorMessage();
      }

      handleSwapProduct(productUrl, updateFullPage) {
        return (html) => {
          this.productModal?.remove();

          const selector = updateFullPage
            ? "product-info[id^='MainProduct']"
            : 'product-info';
          const variant = this.getSelectedVariant(html.querySelector(selector));
          this.updateURL(productUrl, variant?.id);

          if (updateFullPage) {
            document.querySelector('head title').innerHTML =
              html.querySelector('head title').innerHTML;

            HTMLUpdateUtility.viewTransition(
              document.querySelector('main'),
              html.querySelector('main'),
              this.preProcessHtmlCallbacks,
              this.postProcessHtmlCallbacks,
            );
          } else {
            HTMLUpdateUtility.viewTransition(
              this,
              html.querySelector('product-info'),
              this.preProcessHtmlCallbacks,
              this.postProcessHtmlCallbacks,
            );
          }
        };
      }

      getAllVariants() {
        return new Promise((resolve, reject) => {
          const request = indexedDB.open('ShopifyProductDB', 1);

          request.onsuccess = function(event) {
            const db = event.target.result;
            const transaction = db.transaction(['products'], 'readonly');
            const store = transaction.objectStore('products');

            const getAllRequest = store.getAll();
            getAllRequest.onsuccess = function(e) {
              const allVariants = e.target.result;
              resolve(allVariants);
            };

            getAllRequest.onerror = function(e) {
              reject(e.target.error);
            };
          };

          request.onerror = function(event) {
            reject(event.target.error);
          };
        });
      }

      getVariantByOptions(option1Val, option2Val = null, option3Val = null) {
        return new Promise((resolve, reject) => {
          const request = indexedDB.open('ShopifyProductDB', 1);

          request.onsuccess = function(event) {
            const db = event.target.result;
            const transaction = db.transaction(['products'], 'readonly');
            const store = transaction.objectStore('products');

            const getAllRequest = store.getAll();
            getAllRequest.onsuccess = function(e) {
              const allVariants = e.target.result;
              const foundVariant = allVariants.find((variant) => {
                if (option1Val && variant.option1 !== option1Val) return false;
                if (option2Val && variant.option2 !== option2Val) return false;
                if (option3Val && variant.option3 !== option3Val) return false;
                return true;
              });
              resolve(foundVariant || null);
            };

            getAllRequest.onerror = function(e) {
              reject(e.target.error);
            };
          };

          request.onerror = function(event) {
            reject(event.target.error);
          };
        });
      }

      renderProductInfo({ requestUrl, targetId, callback }) {
        const selectedOptions = Array.from(
          document.querySelectorAll('variant-selects option[selected]')
        ).map(opt => opt.value);
        const [option1Val, option2Val, option3Val] = selectedOptions;
        this.getVariantByOptions(option1Val || null, option2Val || null, option3Val || null)
          .then((variant) => {
            if (variant) {
              this.updateProductInfo(variant);
              return;
            }
            this.fallbackFetch(requestUrl, targetId, callback);
          })
          .catch((error) => {
            this.fallbackFetch(requestUrl, targetId, callback);
          });
      }

      fallbackFetch(requestUrl, targetId, callback) {
        this.abortController?.abort();
        this.abortController = new AbortController();

        fetch(requestUrl, { signal: this.abortController.signal })
          .then((response) => response.text())
          .then((responseText) => {
            this.pendingRequestUrl = null;
            const html = new DOMParser().parseFromString(responseText, 'text/html');
            callback(html);
          })
          .then(() => {
            // set focus to last clicked option value
            document.querySelector(`#${targetId}`)?.focus();
          })
          .catch((error) => {
            if (error.name === 'AbortError') {
              console.log('Fetch aborted by user');
            } else {
              console.error(error);
            }
          });
      }

      formatPrice(priceInCents, currency = 'USD', locale = 'en-US') {
        const priceInDollars = priceInCents / 100;
        return new Intl.NumberFormat(locale, {
          style: 'currency',
          currency: currency,
        }).format(priceInDollars);
      }

      updateVariantOptions(allVariants) {
        const variantSelects = document.querySelector('variant-selects');
        if (!variantSelects) return;

        const selects = variantSelects.querySelectorAll('select');
        if (selects.length < 2) return;

        const [select1, select2, select3] = selects;
        const selectedOption1 = select1.value;
        const selectedOption2 = select2.value;
        const selectedOption3 = select3 ? select3.value : null;

        select2.querySelectorAll('option').forEach(option => {
          const tempOption2 = option.value;
          const isAvailable = allVariants.some(variant => {
            return variant.option1 === selectedOption1 && variant.option2 === tempOption2 &&
              (!select3 || variant.option3 === selectedOption3);
          });

          const cleanText = option.dataset.cleanValue;
          option.textContent = isAvailable ? cleanText : `${cleanText} - Unavailable`;
        });

        select1.querySelectorAll('option').forEach(option => {
          const tempOption1 = option.value;
          const isAvailable = allVariants.some(variant => {
            return variant.option1 === tempOption1 && variant.option2 === selectedOption2 &&
              (!select3 || variant.option3 === selectedOption3);
          });

          const cleanText = option.dataset.cleanValue;
          option.textContent = isAvailable ? cleanText : `${cleanText} - Unavailable`;
        });

        if (select3) {
          select3.querySelectorAll('option').forEach(option => {
            const tempOption3 = option.value;
            const isAvailable = allVariants.some(variant => {
              return variant.option1 === selectedOption1 && variant.option2 === selectedOption2 &&
                variant.option3 === tempOption3;
            });

            const cleanText = option.dataset.cleanValue;
            option.textContent = isAvailable ? cleanText : `${cleanText} - Unavailable`;
          });
        }
      }

      updateProductInfo(variant){
          const productTitle = document.getElementById('product-title');
          const productSKU = document.getElementById('product-sku');

          //Update SKU
          productSKU.innerHTML = variant.sku;
          //Update Price
          this.updateProductPrice(variant);
          //Update Options
          this.getAllVariants()
            .then((allVariants) => {
              this.updateVariantOptions(allVariants);
          })
          .catch((error) => {
            console.error('Error getting variants:', error);
          });
          //Update Selected Variant
          var variantInputId = this.productForm?.variantIdInput;
          variantInputId.value = variant.id;
          //Update image
          if (variant.pv_image) {
            const thumbnailList = document.querySelector('slider-component.thumbnail-slider');
            if (thumbnailList) {
              const thumbnail = Array.from(thumbnailList.querySelectorAll('li')).find(li => {
                const img = li.querySelector('img');
                return img && img.src.includes(variant.pv_image);
              });
              if (thumbnail) {
                thumbnailList.querySelectorAll('button[aria-current="true"]').forEach(button => {
                  button.setAttribute('aria-current', 'false');
                });
                const button = thumbnail.querySelector('button');
                if (button) {
                  button.setAttribute('aria-current', 'true');
                  button.click();
                }
                thumbnail.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
              }
            }
          }
          // Update accordion info based on attribute data-meta
          const accordionContent = document.querySelector('#ProductAccordion--template--23782799343981__main');
          if (accordionContent) {
            const metaElements = accordionContent.querySelectorAll('[data-meta]');
            metaElements.forEach((metaElement) => {
              const metaKey = metaElement.getAttribute('data-meta');
              const metaValue = variant.metafields[metaKey];
              if (metaValue) {
                const valueElement = metaElement.querySelector('.value');
                if (valueElement) {
                  valueElement.textContent = metaValue;
                }
              }
            });
          }
          //Update the button
          const quantityBuyBlock = document.querySelector('.quantity-buy-block');

          if (quantityBuyBlock) {
            const requestAQuote = variant.metafields['product-availability'].toLowerCase() === 'call for pricing';

            if (requestAQuote) {
              const existingButton = quantityBuyBlock.querySelector('.request-a-quote');
              if (existingButton) {
                existingButton.classList.remove('twcss-hidden');
              } else {
                const button = document.createElement('button');
                button.className = "request-a-quote button button--full-width button--primary !twcss-min-h-reset twcss-p-4 twcss-text-base";
                button.setAttribute('data-product-sku', variant.sku);
                button.textContent = (window.variantStrings && window.variantStrings.requestAQuote) || 'Request a Quote';
                quantityBuyBlock.prepend(button);
              }
              quantityBuyBlock.querySelectorAll('.product-form__quantity, .product-form-wrapper').forEach(element => {
                element.classList.add('twcss-hidden');
              });
            } else {
              quantityBuyBlock.querySelectorAll('.product-form__quantity, .product-form-wrapper').forEach(element => {
                element.classList.remove('twcss-hidden');
              });
              const existingButton = quantityBuyBlock.querySelector('.request-a-quote');
              if (existingButton) {
                existingButton.classList.add('twcss-hidden');
              }
              this.productForm?.toggleSubmitButton(!variant.available ?? true, window.variantStrings.soldOut);
            }
          }

      }

      updateProductPrice(variant){
        const priceContainer = document.querySelector('.product__info-container .price');
        const mapPricingWrapper = document.querySelector('.product__info-container .map-pricing-text-wrapper');
        const mapPricing = variant.metafields['map-pricing'] * 100;
        const requestAQuote = variant.metafields['product-availability'].toLowerCase() === 'call for pricing';

        const priceUnit = variant.metafields['package-size'];
        const priceUnitContainer = document.querySelector('.product__info-container .price-unit');
        priceUnitContainer.innerHTML = priceUnit ? priceUnit : 'each';

        if(mapPricing > variant.price || requestAQuote){
          priceContainer.classList.add('twcss-hidden');
          mapPricingWrapper.classList.remove('twcss-hidden');
        }else{
          priceContainer.classList.remove('twcss-hidden');
          mapPricingWrapper.classList.add('twcss-hidden');
          if(variant.compare_at_price > variant.price){
            const regularPriceContainer = priceContainer.querySelector('.price__sale .price-item--regular');
            const salePriceContainer = priceContainer.querySelector('.price__sale .price-item--sale');
            const discountContainer = priceContainer.querySelector('.price__sale .discount');
            regularPriceContainer.innerHTML = this.formatPrice(variant.compare_at_price);
            salePriceContainer.innerHTML = this.formatPrice(variant.price);
            let discount = variant.compare_at_price - variant.price;
            let discount_percentage = (discount / variant.compare_at_price) * 100;
            discountContainer.innerHTML = discount_percentage + '% off';
            priceContainer.classList.add('price--on-sale')
          }else{
            priceContainer.classList.remove('price--on-sale')
            const regularPriceContainer = priceContainer.querySelector('.price__regular .price-item--regular');
            regularPriceContainer.innerHTML = this.formatPrice(variant.price);
          }
        }
      }

      getSelectedVariant(productInfoNode) {
        const selectedVariant = productInfoNode.querySelector(
          'variant-selects [data-selected-variant]',
        )?.innerHTML;
        return !!selectedVariant ? JSON.parse(selectedVariant) : null;
      }

      buildRequestUrlWithParams(
        url,
        optionValues,
        shouldFetchFullPage = false,
      ) {
        const params = [];

        !shouldFetchFullPage && params.push(`section_id=${this.sectionId}`);

        if (optionValues.length) {
          params.push(`option_values=${optionValues.join(',')}`);
        }

        return `${url}?${params.join('&')}`;
      }

      updateOptionValues(html) {
        const variantSelects = html.querySelector('variant-selects');
        if (variantSelects) {
          HTMLUpdateUtility.viewTransition(
            this.variantSelectors,
            variantSelects,
            this.preProcessHtmlCallbacks,
          );
        }
      }

      handleUpdateProductInfo(productUrl) {
        return (html) => {
          const variant = this.getSelectedVariant(html);

          this.pickupAvailability?.update(variant);
          this.updateOptionValues(html);
          this.updateURL(productUrl, variant?.id);
          this.updateVariantInputs(variant?.id);

          if (!variant) {
            this.setUnavailable();
            return;
          }

          this.updateMedia(html, variant?.featured_media?.id);

          const updateSourceFromDestination = (
            id,
            shouldHide = (source) => false,
          ) => {
            const source = html.getElementById(`${id}-${this.sectionId}`);
            const destination = this.querySelector(
              `#${id}-${this.dataset.section}`,
            );
            if (source && destination) {
              destination.innerHTML = source.innerHTML;
              destination.classList.toggle('hidden', shouldHide(source));
            }
          };

          updateSourceFromDestination('price');
          updateSourceFromDestination('Sku', ({ classList }) =>
            classList.contains('hidden'),
          );
          updateSourceFromDestination(
            'Inventory',
            ({ innerText }) => innerText === '',
          );
          updateSourceFromDestination('Volume');
          updateSourceFromDestination('Price-Per-Item', ({ classList }) =>
            classList.contains('hidden'),
          );

          this.updateQuantityRules(this.sectionId, html);
          this.querySelector(
            `#Quantity-Rules-${this.dataset.section}`,
          )?.classList.remove('hidden');
          this.querySelector(
            `#Volume-Note-${this.dataset.section}`,
          )?.classList.remove('hidden');

          this.productForm?.toggleSubmitButton(
            html
              .getElementById(`ProductSubmitButton-${this.sectionId}`)
              ?.hasAttribute('disabled') ?? true,
            window.variantStrings.soldOut,
          );

          publish(PUB_SUB_EVENTS.variantChange, {
            data: {
              sectionId: this.sectionId,
              html,
              variant,
            },
          });
        };
      }

      updateVariantInputs(variantId) {
        this.querySelectorAll(
          `#product-form-${this.dataset.section}, #product-form-installment-${this.dataset.section}`,
        ).forEach((productForm) => {
          const input = productForm.querySelector('input[name="id"]');
          input.value = variantId ?? '';
          input.dispatchEvent(new Event('change', { bubbles: true }));
        });
      }

      updateURL(url, variantId) {
        this.querySelector('share-button')?.updateUrl(
          `${window.shopUrl}${url}${variantId ? `?variant=${variantId}` : ''}`,
        );

        if (this.dataset.updateUrl === 'false') return;
        window.history.replaceState(
          {},
          '',
          `${url}${variantId ? `?variant=${variantId}` : ''}`,
        );
      }

      setUnavailable() {
        this.productForm?.toggleSubmitButton(
          true,
          window.variantStrings.unavailable,
        );

        const selectors = [
          'price',
          'Inventory',
          'Sku',
          'Price-Per-Item',
          'Volume-Note',
          'Volume',
          'Quantity-Rules',
        ]
          .map((id) => `#${id}-${this.dataset.section}`)
          .join(', ');
        document
          .querySelectorAll(selectors)
          .forEach(({ classList }) => classList.add('hidden'));
      }

      updateMedia(html, variantFeaturedMediaId) {
        if (!variantFeaturedMediaId) return;

        const mediaGallerySource = this.querySelector('media-gallery ul');
        const mediaGalleryDestination = html.querySelector(`media-gallery ul`);

        const refreshSourceData = () => {
          if (this.hasAttribute('data-zoom-on-hover')) enableZoomOnHover(2);
          const mediaGallerySourceItems = Array.from(
            mediaGallerySource.querySelectorAll('li[data-media-id]'),
          );
          const sourceSet = new Set(
            mediaGallerySourceItems.map((item) => item.dataset.mediaId),
          );
          const sourceMap = new Map(
            mediaGallerySourceItems.map((item, index) => [
              item.dataset.mediaId,
              { item, index },
            ]),
          );
          return [mediaGallerySourceItems, sourceSet, sourceMap];
        };

        if (mediaGallerySource && mediaGalleryDestination) {
          let [mediaGallerySourceItems, sourceSet, sourceMap] =
            refreshSourceData();
          const mediaGalleryDestinationItems = Array.from(
            mediaGalleryDestination.querySelectorAll('li[data-media-id]'),
          );
          const destinationSet = new Set(
            mediaGalleryDestinationItems.map(({ dataset }) => dataset.mediaId),
          );
          let shouldRefresh = false;

          // add items from new data not present in DOM
          for (let i = mediaGalleryDestinationItems.length - 1; i >= 0; i--) {
            if (
              !sourceSet.has(mediaGalleryDestinationItems[i].dataset.mediaId)
            ) {
              mediaGallerySource.prepend(mediaGalleryDestinationItems[i]);
              shouldRefresh = true;
            }
          }

          // remove items from DOM not present in new data
          for (let i = 0; i < mediaGallerySourceItems.length; i++) {
            if (
              !destinationSet.has(mediaGallerySourceItems[i].dataset.mediaId)
            ) {
              mediaGallerySourceItems[i].remove();
              shouldRefresh = true;
            }
          }

          // refresh
          if (shouldRefresh)
            [mediaGallerySourceItems, sourceSet, sourceMap] =
              refreshSourceData();

          // if media galleries don't match, sort to match new data order
          mediaGalleryDestinationItems.forEach(
            (destinationItem, destinationIndex) => {
              const sourceData = sourceMap.get(destinationItem.dataset.mediaId);

              if (sourceData && sourceData.index !== destinationIndex) {
                mediaGallerySource.insertBefore(
                  sourceData.item,
                  mediaGallerySource.querySelector(
                    `li:nth-of-type(${destinationIndex + 1})`,
                  ),
                );

                // refresh source now that it has been modified
                [mediaGallerySourceItems, sourceSet, sourceMap] =
                  refreshSourceData();
              }
            },
          );
        }

        // set featured media as active in the media gallery
        this.querySelector(`media-gallery`)?.setActiveMedia?.(
          `${this.dataset.section}-${variantFeaturedMediaId}`,
          true,
        );

        // update media modal
        const modalContent = this.productModal?.querySelector(
          `.product-media-modal__content`,
        );
        const newModalContent = html.querySelector(
          `product-modal .product-media-modal__content`,
        );
        if (modalContent && newModalContent)
          modalContent.innerHTML = newModalContent.innerHTML;
      }

      setQuantityBoundries() {
        const data = {
          cartQuantity: this.quantityInput.dataset.cartQuantity
            ? parseInt(this.quantityInput.dataset.cartQuantity)
            : 0,
          min: this.quantityInput.dataset.min
            ? parseInt(this.quantityInput.dataset.min)
            : 1,
          max: this.quantityInput.dataset.max
            ? parseInt(this.quantityInput.dataset.max)
            : null,
          step: this.quantityInput.step ? parseInt(this.quantityInput.step) : 1,
        };

        let min = data.min;
        const max = data.max === null ? data.max : data.max - data.cartQuantity;
        if (max !== null) min = Math.min(min, max);
        if (data.cartQuantity >= data.min) min = Math.min(min, data.step);

        this.quantityInput.min = min;

        if (max) {
          this.quantityInput.max = max;
        } else {
          this.quantityInput.removeAttribute('max');
        }
        this.quantityInput.value = min;

        publish(PUB_SUB_EVENTS.quantityUpdate, undefined);
      }

      fetchQuantityRules() {
        const currentVariantId = this.productForm?.variantIdInput?.value;
        if (!currentVariantId) return;

        this.querySelector(
          '.quantity__rules-cart .loading__spinner',
        ).classList.remove('hidden');
        fetch(
          `${this.dataset.url}?variant=${currentVariantId}&section_id=${this.dataset.section}`,
        )
          .then((response) => response.text())
          .then((responseText) => {
            const html = new DOMParser().parseFromString(
              responseText,
              'text/html',
            );
            this.updateQuantityRules(this.dataset.section, html);
          })
          .catch((e) => console.error(e))
          .finally(() =>
            this.querySelector(
              '.quantity__rules-cart .loading__spinner',
            ).classList.add('hidden'),
          );
      }

      updateQuantityRules(sectionId, html) {
        if (!this.quantityInput) return;
        this.setQuantityBoundries();

        const quantityFormUpdated = html.getElementById(
          `Quantity-Form-${sectionId}`,
        );
        const selectors = [
          '.quantity__input',
          '.quantity__rules',
          '.quantity__label',
        ];
        for (let selector of selectors) {
          const current = this.quantityForm.querySelector(selector);
          const updated = quantityFormUpdated.querySelector(selector);
          if (!current || !updated) continue;
          if (selector === '.quantity__input') {
            const attributes = [
              'data-cart-quantity',
              'data-min',
              'data-max',
              'step',
            ];
            for (let attribute of attributes) {
              const valueUpdated = updated.getAttribute(attribute);
              if (valueUpdated !== null) {
                current.setAttribute(attribute, valueUpdated);
              } else {
                current.removeAttribute(attribute);
              }
            }
          } else {
            current.innerHTML = updated.innerHTML;
          }
        }
      }

      get productForm() {
        return this.querySelector(`product-form`);
      }

      get productModal() {
        return document.querySelector(`#ProductModal-${this.dataset.section}`);
      }

      get pickupAvailability() {
        return this.querySelector(`pickup-availability`);
      }

      get variantSelectors() {
        return this.querySelector('variant-selects');
      }

      get relatedProducts() {
        const relatedProductsSectionId = SectionId.getIdForSection(
          SectionId.parseId(this.sectionId),
          'related-products',
        );
        return document.querySelector(
          `product-recommendations[data-section-id^="${relatedProductsSectionId}"]`,
        );
      }

      get quickOrderList() {
        const quickOrderListSectionId = SectionId.getIdForSection(
          SectionId.parseId(this.sectionId),
          'quick_order_list',
        );
        return document.querySelector(
          `quick-order-list[data-id^="${quickOrderListSectionId}"]`,
        );
      }

      get sectionId() {
        return this.dataset.originalSection || this.dataset.section;
      }
    },
  );
}
